/**
 * Shared utilities for all Monamodi policy amendments
 *
 * This module provides common functionality for processing amendments including:
 * - Quote creation and attachment
 * - Policy note sanitization and validation
 * - Common amendment workflow
 */

import { createQuote, changeQuote, policyNotes } from '../../../../../lib/requests.es6';
import { getUserFullname, getUserGuid } from '../../../../../lib/access.es6';

// ============================================================================
// CONSTANTS
// ============================================================================

// Maximum safe length for policy notes (MySQL column limit)
export const MAX_NOTE_LENGTH = 250;

// ============================================================================
// TEXT SANITIZATION
// ============================================================================

/**
 * Sanitize text to remove special characters that MySQL latin1 encoding doesn't support
 * Replaces UTF-8 characters with ASCII equivalents
 *
 * @param {String} text - Text to sanitize
 * @returns {String} - Sanitized text safe for MySQL latin1
 */
export const sanitizeText = (text) => {
  return text
    .replace(/→/g, '->')  // Replace arrow with ASCII
    .replace(/–/g, '-')   // Replace en-dash with hyphen
    .replace(/—/g, '-')   // Replace em-dash with hyphen
    .replace(/'/g, "'")   // Replace smart quotes
    .replace(/'/g, "'")
    .replace(/"/g, '"')
    .replace(/"/g, '"')
    .replace(/•/g, '*')   // Replace bullet with asterisk
    .replace(/…/g, '...')  // Replace ellipsis
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\x7F]/g, ''); // Remove any remaining non-ASCII characters
};

/**
 * Truncate note if it exceeds max length, ensuring it ends gracefully
 *
 * @param {String} note - Note text to truncate
 * @param {Number} maxLength - Maximum allowed length
 * @returns {String} - Truncated note
 */
export const truncateNote = (note, maxLength = MAX_NOTE_LENGTH) => {
  if (note.length <= maxLength) {
    return note;
  }

  // Truncate and add ellipsis
  const truncated = note.substring(0, maxLength - 3) + '...';
  console.warn(`Policy note truncated from ${note.length} to ${truncated.length} characters`);
  return truncated;
};

/**
 * Prepare policy note for database storage
 * Sanitizes special characters and enforces length limits
 *
 * @param {String} note - Raw note text
 * @returns {String} - Sanitized and validated note ready for storage
 */
export const preparePolicyNote = (note) => {
  // 1. Sanitize special characters
  let sanitized = sanitizeText(note);

  // 2. Truncate if exceeds max length
  sanitized = truncateNote(sanitized, MAX_NOTE_LENGTH);

  // 3. Log for monitoring
  console.log(`Policy note prepared: ${sanitized.length} / ${MAX_NOTE_LENGTH} chars`);

  return sanitized;
};

// ============================================================================
// AMENDMENT PROCESSING
// ============================================================================

/**
 * Process a policy amendment using the standard workflow:
 * 1. Create new quotation
 * 2. Change policy quotation
 * 3. Add policy note
 *
 * @param {Object} params - Amendment parameters
 * @param {Object} params.quotationPayload - The new quotation data
 * @param {Object} params.policy - Current policy object
 * @param {String} params.noteContent - Policy note content (will be sanitized automatically)
 * @param {Function} params.updateStatus - Optional callback to update loading status
 *   Receives: (step, status) where step is 'quote'|'swap'|'note' and status is 'loading'|'success'|'error'
 * @returns {Promise<Object>} - { success: true/false, noteSuccess: true/false, quoteError, swapError, noteError, quotationGuid }
 * @throws {Error} - If quote creation or swap fails (amendment failed)
 */
export const processAmendment = async ({ quotationPayload, policy, noteContent, updateStatus }) => {
  let newQuotationGuid = null;
  let noteSuccess = false;
  let noteError = null;

  try {
    // Step 1: Create new quotation
    if (updateStatus) updateStatus('quote', 'loading');
    const quoteResponse = await createQuote(quotationPayload);
    newQuotationGuid = quoteResponse.data.guid;

    if (!newQuotationGuid) {
      throw new Error('Failed to create quotation - no GUID returned');
    }
    if (updateStatus) updateStatus('quote', 'success');

    // Step 2: Change policy quotation
    if (updateStatus) updateStatus('swap', 'loading');
    const changePayload = {
      policy_guid: policy.guid,
      quotation_guid: newQuotationGuid,
      customer_guid: policy.customer_guid
    };

    // Only include start_date if policy is not Rejected or Un_Confirmed
    const policyStatus = policy.status;
    if (policyStatus !== 'Rejected' && policyStatus !== 'Un_Confirmed') {
      changePayload.start_date = policy.start_date;
    }

    await changeQuote(changePayload);
    if (updateStatus) updateStatus('swap', 'success');

    // Step 3: Add policy note (separate try-catch to allow partial success)
    if (updateStatus) updateStatus('note', 'loading');
    try {
      // Prepare note (sanitize and validate)
      const sanitizedNote = preparePolicyNote(noteContent);

      const notePayload = {
        note: sanitizedNote,
        policy_guid: policy.guid,
        web_user: getUserFullname(),
        web_user_guid: getUserGuid()
      };

      await policyNotes(notePayload);
      noteSuccess = true;
      if (updateStatus) updateStatus('note', 'success');
    } catch (noteErr) {
      console.error('Note save failed (amendment still succeeded):', noteErr);
      noteSuccess = false;
      noteError = noteErr.response && noteErr.response.data && noteErr.response.data.message
        ? noteErr.response.data.message
        : 'Failed to save audit note';
      if (updateStatus) updateStatus('note', 'error');
    }

    // Return success (even if note failed, amendment succeeded)
    return {
      success: true,
      noteSuccess,
      quotationGuid: newQuotationGuid,
      quoteError: null,
      swapError: null,
      noteError
    };

  } catch (error) {
    console.error('Amendment processing error:', error);
    const errorMessage = error.response && error.response.data && error.response.data.message
      ? error.response.data.message
      : error.message || 'Failed to process amendment. Please try again.';

    // Determine which step failed
    const quoteError = !newQuotationGuid ? errorMessage : null;
    const swapError = newQuotationGuid && !noteSuccess && !noteError ? errorMessage : null;

    if (updateStatus) {
      if (quoteError) updateStatus('quote', 'error');
      if (swapError) updateStatus('swap', 'error');
    }

    throw new Error(errorMessage);
  }
};

// ============================================================================
// NOTE FORMATTING HELPERS
// ============================================================================

/**
 * Format currency amount for display
 * @param {Number} amountInCents - Amount in cents
 * @returns {String} - Formatted amount (e.g., "123.45")
 */
export const formatCurrency = (amountInCents) => {
  return ((amountInCents || 0) / 100).toFixed(2);
};

/**
 * Build a compact policy header for notes
 * @param {String} policyNumber - Policy number
 * @returns {String} - Formatted header
 */
export const buildPolicyHeader = (policyNumber) => {
  return `Policy: ${policyNumber}`;
};

/**
 * Build premium change summary
 * @param {Number} oldPremium - Old premium amount
 * @param {Number} newPremium - New premium amount
 * @returns {String} - Formatted premium summary
 */
export const buildPremiumSummary = (oldPremium, newPremium) => {
  const difference = newPremium - oldPremium;
  const changeType = difference > 0 ? 'Increase' : (difference < 0 ? 'Decrease' : 'No Change');

  return `Premium:
Old: BWP ${formatCurrency(oldPremium * 100)}
New: BWP ${formatCurrency(newPremium * 100)}
${changeType}: BWP ${Math.abs(difference).toFixed(2)}`;
};
